<template>
    <Dialog
        :value="true"
        :header-text="$t('Add company event')"
        :width="510"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <v-row
            v-if="form_data"
        >
            <v-col
                cols="12"
                class="pt-0"
                v-if="canSelectCompany"

            >
                <v-select
                    v-model="form_data.company_id"
                    :label="$t('Company')"
                    :items="selectableCompanyAccounts"
                    item-text="name"
                    item-value="id"
                    outlined
                ></v-select>
            </v-col>
            <v-col
                cols="12"
                class="pt-0"
            >
                <v-row no-gutters>
                    <v-col class="col-12">
                        <v-text-field
                            v-model="form_data.name"
                            :label="$t('Name') + ' *'"
                            :error-messages="errors['name']"
                            outlined
                        ></v-text-field>
                    </v-col>
                </v-row>
            </v-col>
            <v-col
                v-if="isFlextimeEnabled || form_data.type === 'company_free_time'"
                cols="12"
                class="pt-0"
            >
                <v-radio-group
                    class="mt-0 pt-0"
                    v-model="form_data.type"
                    :error-messages="errors['type']"
                    row
                    hide-details
                >
                    <v-radio
                        color="primary"
                        value="company_event"
                    >
                        <template v-slot:label>
                            <div class="body-2">
                                {{ $t('Info') }}
                            </div>
                        </template>
                    </v-radio>
                    <v-radio
                        v-if="isFlextimeEnabled || form_data.type === 'company_free_time'"
                        color="primary"
                        :disabled="!isFlextimeEnabled"
                        value="company_free_time"
                    >
                        <template v-slot:label>
                            <div class="body-2">
                                {{ $t('Holiday') }}
                            </div>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-col>
            <v-col
                cols="12"
                class="pt-0"
            >
                <v-checkbox
                    v-model="isAllDaySelected"
                    :label="$t('All day(s)')"
                    hide-details
                    @change="onChangeIsAllDay"
                />
            </v-col>
            <v-col
                cols="4"
                class="pt-1 pr-1"
            >
                <date-picker
                    v-model="form_data.date_from"
                    :label="$t('Start date') + ' *'"
                    :error-messages="errors['date_from']"
                    :max="isAllDaySelected ? form_data.date_until : null"
                    @input="onSetDateFrom"
                />
            </v-col>
            <template v-if="isAllDaySelected">
                <v-col
                    cols="4"
                    class="pt-1 pl-1"
                >
                    <date-picker
                        v-model="form_data.date_until"
                        :label="$t('End date') + ' *'"
                        :error-messages="errors['date_until']"
                        :min="form_data.date_from"
                        @input="onSetDateUntil"
                    />
                </v-col>
                <v-col cols="1" />
            </template>
            <v-col
                v-else
                cols="5"
                class="d-flex pt-1 align-center"
            >
                <div class="pr-1">
                    <text-field
                        :value="timeFromFormatted"
                        :label="$t('From')"
                        @input="timeFromFormatted = $event"
                        @blur="setFormattedTimeFrom"
                    />
                </div>
                <div class="pl-1">
                    <text-field
                        :value="timeUntilFormatted"
                        :label="$t('Until')"
                        @input="timeUntilFormatted = $event"
                        @blur="setFormattedTimeUntil"
                    />
                </div>
            </v-col>
            <v-col cols="3" class="pt-1">
                <div class="caption g5--text d-flex">
                    <span class="mr-1">{{ isAllDaySelected ? $t('Days') : $t('Hours') }}</span>
                </div>
                <div class="title mt-3">
                    {{ isAllDaySelected ? totalDays : totalHours }}
                </div>
            </v-col>
            <v-col
                cols="12"
            >
                <v-row no-gutters>
                    <v-col class="col-12">
                        <v-text-field
                            v-model="form_data.link"
                            :label="$t('Link')"
                            :error-messages="errors['link']"
                            outlined
                        ></v-text-field>
                    </v-col>
                </v-row>
            </v-col>
            <v-col
                cols="12"
            >
                <text-area-field
                    :value="form_data.comment"
                    :max-length="255"
                    :label="$t('Notes')"
                    :error-messages="errors['comment']"
                    @input="form_data.comment = $event; errors['comment'] = null"
                />
            </v-col>
        </v-row>
        <template slot="footer">
            <icon-button
                v-if="form_data.id"
                icon="Trash2"
                @click="deleteCompanyEvent(props.calendar)">
            </icon-button>
            <v-spacer/>
            <secondary-button
                @click="$emit('close')"
            >
                {{ $t('Cancel') }}
            </secondary-button>
            <primary-button
                @click="saveCompanyEvent"
            >
                {{ $t('Save') }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import {$t} from "@/i18n";
import {computed, ref, reactive} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import {
    formatDaysFromDuration, formatSysDate, formatSysDateTime, formatSysTime,
    formatTime,
    formatTimeDuration, parseSysDateTime, parseSysTime,
    parseTime,
    parseTimeDuration
} from "@/plugins/formatting";
import VacationDurationList from "@/components/Flextime/VacationDurationList.vue";
import * as ValidationModule from "@/modules/validation/validation";
import CalendarEventModule from "@/store/modules/calendar_event"
import DialogModule from "@/store/modules/dialog";
import AccountModule, {actions as accountModule} from "@/store/modules/account";

export default {
    name: "CompanyCalendarEventModal",
    components: {
        VacationDurationList,
        Dialog
    },
    props: {
        calendar: {
            type: Object,
            default: null
        },
        selectedAccount: {
            type: Object,
            default: null
        },
        canSelectCompany: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const { available_company_accounts, current_account } = AccountModule();
        const {deleteCalendarEvent, saveCompanyCalendarEvent} = CalendarEventModule();
        const {openConfirmDialog, setConfirmOptions} = DialogModule();

        const form_data = ref(null);
        const isAllDaySelected = ref(true);
        const hours = ref(null);
        const formattedHours = ref("");

        const timeFrom = ref('09:00:00');
        const timeFromFormatted = ref('09:00');
        const timeUntil = ref('17:00:00');
        const timeUntilFormatted = ref('17:00');

        const errors = ref({});

        const selectableCompanyAccounts = computed(() => {
            return available_company_accounts.value.filter(company => {
                const currentMember = company.members.find(x => x.id === current_account.value.id)
                return currentMember && ['admin', 'owner'].includes(currentMember.role)
            })
        })

        const saveCompanyEvent = async () => {
            try {
                let dateFromToSave;
                let dateUntilToSave
                if (form_data.value.date_from != null) {
                    dateFromToSave = getSaveDateFrom();
                } else {
                    dateFromToSave = form_data.value.date_from;
                }
                if (form_data.value.date_until != null) {
                    dateUntilToSave = getSaveDateUntil();
                } else {
                    dateUntilToSave = form_data.value.date_until;
                }

                const payload = {
                    id: form_data.value.id,
                    company_id: form_data.value.company_id,
                    type: form_data.value.type,
                    name: form_data.value.name,
                    date_from: dateFromToSave,
                    date_until: dateUntilToSave,
                    link: form_data.value.link,
                    comment: form_data.value.comment
                }

                await saveCompanyCalendarEvent(payload);
                ctx.emit('update')
                ctx.emit('close')
            } catch (e) {
                errors.value = ValidationModule.parseErrors(e)
                if (!ValidationModule.hasErrors(errors.value)) {
                    errorDialog.showError(e);
                }
            }
        }

        const deleteCompanyEvent = item => { // after delete close modal, and refresh calendar in calendar.vue
            setConfirmOptions({
                title: $t(
                    "Are you sure that you want to delete this company calendar event?"
                ),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: async () => {
                    try {
                        await deleteCalendarEvent(item.id);
                        ctx.emit('update')
                        ctx.emit('close')
                    } catch (e) {
                        errorDialog.showError(e)
                    }
                }
            });
            openConfirmDialog();
        }

        const onChangeIsAllDay = (value) => {
            hours.value = null;
            formattedHours.value = null;
            isAllDaySelected.value = value;

            if (!isAllDaySelected.value) {
                form_data.value.date_until = form_data.value.date_from;
                errors.value.date_until = null
            }

        }

        const setFormattedTimeFrom = (event) => {
            let value = event.target.value
            value = value.trim()

            let parsedTime = parseTime(value);

            if (parsedTime) {
                let timeDuration = parseTimeDuration(parsedTime)
                if (timeDuration >= (60 * 60 * 23)) {
                    parsedTime = '22:59:00'
                }

                timeFrom.value = parsedTime
            }

            updateFormattedTimeFrom()
            setMinimumTimeForTimeUntil()
        }

        const setFormattedTimeUntil = (event) => {
            let value = event.target.value
            value = value.trim()

            const parsedTime = parseTime(value);
            if (parsedTime) {
                timeUntil.value = parsedTime
            }

            updateFormattedTimeUntil()
            setMinimumTimeForTimeUntil()
        }

        const updateFormattedTimeFrom = async () => {
            timeFromFormatted.value = formatTime(timeFrom.value);
        }

        const updateFormattedTimeUntil = async () => {
            timeUntilFormatted.value = formatTime(timeUntil.value);
        }

        const setMinimumTimeForTimeUntil = () => {
            if (!timeFrom.value) {
                return
            }

            const sysTimeFrom = parseSysTime(timeFrom.value)
            let sysTimeUntil = parseSysTime(timeUntil.value)

            const duration = sysTimeUntil - sysTimeFrom

            if (duration < (60 * 60 * 1000)) {
                sysTimeUntil = sysTimeFrom
                sysTimeUntil.setHours(sysTimeUntil.getHours() + 1)
            }

            timeUntil.value = formatSysTime(sysTimeUntil)
            updateFormattedTimeUntil()
        }

        const totalHours = computed(() => {
            if (!eventDuration.value) {
                return "-"
            }

            return formatTimeDuration(eventDuration.value)
        })

        const totalDays = computed(() => {
            if (!eventDuration.value) {
                return "-"
            }

            return formatDaysFromDuration(eventDuration.value)
        })

        const eventDuration = computed(() => {
            if (!form_data.value.date_from || !form_data.value.date_until) {
                return false
            }

            let dateFromToSave = getSaveDateFrom();
            let dateUntilToSave = getSaveDateUntil();

            let startDate = new Date(dateFromToSave);
            let endDate = new Date(dateUntilToSave);

            return (endDate.getTime() - startDate.getTime()) / 1000;
        })

        const getSaveDateFrom = () => {
            let dateFromToSave = form_data.value.date_from + ' '
            if (isAllDaySelected.value) {
                dateFromToSave += '00:00:00'
            } else {
                dateFromToSave += timeFrom.value
            }

            return dateFromToSave
        }

        const getSaveDateUntil = () => {
            let dateUntilToSave = (isAllDaySelected.value ? form_data.value.date_until : form_data.value.date_from) + ' '

            if (isAllDaySelected.value) {
                dateUntilToSave += '00:00:00'
            } else {
                dateUntilToSave += timeUntil.value
            }

            let dateUntilToSaveDate = parseSysDateTime(dateUntilToSave)
            let currentDateUntilToSaveDate = parseSysDateTime(dateUntilToSave)
            currentDateUntilToSaveDate.setSeconds(dateUntilToSaveDate.getSeconds() - 1)
            if (currentDateUntilToSaveDate.getDate() !== dateUntilToSaveDate.getDate()) {
                dateUntilToSaveDate.setDate(dateUntilToSaveDate.getDate() + 1)
            }

            dateUntilToSaveDate = formatSysDateTime(dateUntilToSaveDate)

            return dateUntilToSaveDate
        }

        const onSetDateFrom = () => {
            errors.value['date_from'] = null;
            if (!isAllDaySelected.value) {
                form_data.value.date_until = form_data.value.date_from;
            }
        }

        const onSetDateUntil = () => {
            errors.value['date_until'] = null;
        }

        const isFlextimeEnabled = computed(() => {
            return accountModule.isFeatureEnabled(form_data.value.company_id, "flextime", false)
        })

        const load_data = function () {
            try {
                form_data.value = Vue.prepare_form_data({
                    id: null,
                    company_id: null,
                    type: 'company_event',
                    name: null,
                    date_from: null,
                    date_until: null,
                    count_free_time_as_vacation: 0,
                    link: null,
                    comment: null,
                })
                if (props.calendar) {
                    let dateUntilToSaveDate = parseSysDateTime(props.calendar.date_until)
                    dateUntilToSaveDate.setSeconds(dateUntilToSaveDate.getSeconds() - 1)
                    dateUntilToSaveDate = formatSysDate(dateUntilToSaveDate)

                    let startDate = new Date(props.calendar.date_from);
                    let endDate = new Date(props.calendar.date_until);
                    let calculateAllDay = (endDate.getTime() - startDate.getTime()) / 1000;
                    if (calculateAllDay >= (60 * 60 * 24)) {
                        isAllDaySelected.value = true;
                    } else {
                        isAllDaySelected.value = props.calendar.is_all_day;
                    }

                    form_data.value.id = props.calendar.id
                    form_data.value.company_id = props.calendar.company_id;
                    form_data.value.type = props.calendar.type;
                    form_data.value.name = props.calendar.name;
                    form_data.value.date_from = props.calendar.date_from.split(' ')[0];
                    form_data.value.date_until = dateUntilToSaveDate;
                    form_data.value.link = props.calendar.link;
                    form_data.value.comment = props.calendar.comment;
                    timeFrom.value = formatSysTime(parseSysDateTime(props.calendar.date_from));
                    timeUntil.value = formatSysTime(parseSysDateTime(props.calendar.date_until));
                    timeFromFormatted.value = formatTime(props.calendar.date_from.split(' ')[1]);
                    timeUntilFormatted.value = formatTime(props.calendar.date_until.split(' ')[1]);
                } else if (props.selectedAccount) {
                    form_data.value.company_id = props.selectedAccount.id;
                } else {
                    form_data.value.company_id = selectableCompanyAccounts.value[0] ? selectableCompanyAccounts.value[0].id : null;
                }
            } catch (e) {
                errorDialog.showError(e);
            }
        };

        load_data()

        return {
            form_data,
            selectableCompanyAccounts,
            saveCompanyEvent,
            onChangeIsAllDay,
            isAllDaySelected,
            eventDuration,
            totalDays,
            totalHours,
            timeFromFormatted,
            timeUntilFormatted,
            setFormattedTimeFrom,
            setFormattedTimeUntil,
            formatTimeDuration,
            onSetDateFrom,
            onSetDateUntil,
            errors,
            deleteCompanyEvent,
            props,
            isFlextimeEnabled
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep textarea {
    height: 120px;
}
</style>